<script lang="ts" setup generic="T extends { id: number | string }">
const MAX_LENGTH = 40;

const props = defineProps<{
  title: string;
  items: T[];
  limit?: number;
  zIndex?: number;
}>();
const emit = defineEmits<{
  (event: 'load', value: T): void;
  (event: 'delete', value: T): void;
  (event: 'update', value: T): void;
  (event: 'create', value: string): void | Promise<void>;
}>();
const visible = defineModel<boolean>('visible');
const slots = defineSlots<{
  limit?: () => VNode[];
  item?: (props: { data: T }) => VNode[];
}>();

const { t } = useI18n();

const savingName = ref('');

const isLimitAvailable = computed(() => !props.limit || props.items.length < props.limit);

const onLoad = (saving: T) => {
  visible.value = false;
  emit('load', saving);
};

const onCreate = async () => {
  await emit('create', savingName.value);

  savingName.value = '';
};

watch(visible, () => {
  savingName.value = '';
});
</script>

<template>
  <Dialog
    style="width: 520px"
    dismissable-mask
    :base-z-index="zIndex"
    :draggable="false"
    modal
    :header="title"
    v-model:visible="visible"
    data-test-id="savings-dialog"
    :pt="{
      pcCloseButton: { root: { 'data-test-id': 'savings-dialog-close' } },
    }"
  >
    <div v-if="items.length" class="flex flex-col gap-2">
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="flex items-center justify-between gap-2 sm:gap-3 w-full overflow-hidden"
        data-test-id="savings-dialog-item"
      >
        <span
          class="text-lg text-overflow-ellipsis overflow-hidden whitespace-nowrap"
        >
          {{ index + 1 }}.
          <slot name="item" :data="item" />
        </span>

        <div class="flex gap-1 sm:gap-2">
          <Button
            v-tooltip.bottom="t('load')"
            icon="pi pi-upload"
            text
            rounded
            @click="onLoad(item)"
            data-test-id="savings-dialog-item-actions-load"
          />
          <Button
            v-tooltip.bottom="t('replace')"
            icon="pi pi-arrow-right-arrow-left"
            text
            rounded
            @click="emit('update', item)"
          />
          <Button
            v-tooltip.bottom="t('delete')"
            icon="pi pi-trash"
            text
            rounded
            @click="emit('delete', item)"
          />
        </div>
      </div>
    </div>
    <p v-else class="text-lg p-3 text-center m-0">
      {{ t('empty') }}
    </p>

    <template #footer v-if="isLimitAvailable || slots.limit">
      <slot name="limit" v-if="!isLimitAvailable" />

      <form v-if="isLimitAvailable" @submit.prevent="onCreate" class="w-full">
        <InputGroup>
          <InputText
            :placeholder="t('placeholder')"
            v-model="savingName"
            :maxlength="MAX_LENGTH"
            data-test-id="label-input"
          />
          <Button
            icon="pi pi-save"
            class="mr-0"
            type="submit"
            :disabled="!savingName"
            data-test-id="submit"
          />
        </InputGroup>
      </form>
    </template>
  </Dialog>
</template>

<i18n>
en:
  delete: Delete
  load: Load
  replace: Replace
  empty: The list of saves is empty
  placeholder: New save name

ru:
  delete: Удалить
  load: Загрузить
  replace: Заменить
  empty: Список сохранений пустой
  placeholder: Название нового сохранения

de:
  delete: Löschen
  load: Herunterladen
  replace: Ersetzen
  empty: Die Speicherliste ist leer
  placeholder: Name des neuen Speichers

es:
  delete: Borrar
  load: Descargar
  replace: Reemplazar
  empty: La lista de guardado está vacía
  placeholder: Nombre del nuevo guardado

fr:
  delete: Supprimer
  load: Télécharger
  replace: Remplacer
  empty: La liste de sauvegarde est vide
  placeholder: Nom de la nouvelle sauvegarde
</i18n>
